import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, map, of } from "rxjs";
import {
  createInvitation,
  createInvitationFailure,
  createInvitationSuccess,
  deleteInvitation,
  deleteInvitationFailure,
  deleteInvitationSuccess,
  loadInvitations,
  loadInvitationsFailure,
  loadInvitationsSuccess
} from "@organizations/store/actions";
import { HttpErrorResponse } from "@angular/common/http";
import { NotificationService } from "@core/services";
import { InvitationService } from "@organizations/services";

@Injectable()
export class InvitationEffects {
  loadInvitations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadInvitations),
      concatMap(({ scope, entityId }) =>
        this.invitationService.getInvitations(scope, entityId).pipe(
          map((invitations) => loadInvitationsSuccess({ invitations })),
          catchError((error: HttpErrorResponse) =>
            of(loadInvitationsFailure({ status: error.status }))
          )
        )
      )
    );
  });

  createInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createInvitation),
      concatMap(({ invitation }) =>
        this.invitationService.createInvitation(invitation).pipe(
          map((invitation) => createInvitationSuccess({ invitation })),
          catchError((error: HttpErrorResponse) =>
            of(createInvitationFailure({ status: error.status }))
          )
        )
      )
    );
  });

  deleteInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteInvitation),
      concatMap(({ invitation }) =>
        this.invitationService.deleteInvitation(invitation.id).pipe(
          map(() => deleteInvitationSuccess({ invitationId: invitation.id })),
          catchError((error: HttpErrorResponse) =>
            of(deleteInvitationFailure({ status: error.status }))
          )
        )
      )
    );
  });

  private getHttpErrorKey(status?: number): string {
    const errorKeyByStatus: Record<number, string> = {
      400: "http_error.400",
      401: "http_error.401",
      403: "http_error.403",
      404: "http_error.404",
      409: "http_error.409"
    };

    return errorKeyByStatus[status ?? 500] ?? "http_error.500";
  }

  notifyInvitationFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loadInvitationsFailure, createInvitationFailure, deleteInvitationFailure),
        map(({ status }) => {
          const key = this.getHttpErrorKey(status);
          this.notificationService.showTranslatedNotification(key, "warning");
          return key;
        })
      );
    },
    { dispatch: false }
  );

  notifyCreateInvitationSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(createInvitationSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.invitation_create_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  notifyDeleteInvitationSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteInvitationSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.invitation_delete_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private invitationService: InvitationService,
    private notificationService: NotificationService
  ) {}
}
